import React, { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { LayoutChangeEvent, View } from 'react-native'
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'

import styled, { useTheme } from 'styled-components/native'

import { AlertBannerTypes } from '@lyrahealth-inc/shared-app-logic/src/features/alerts/AlertBannerTypes'
import { getBreakpointValue } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/breakpoints'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { UnstyledIconButton } from '../../atoms/unstyledIconButton/UnstyledIconButton'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type AlertBannerHandle = {
  open: () => void
  close: () => void
}

export type AlertBannerProps = {
  autoDismissTimer?: number
  contents: ReactNode
  dismissHandler?: () => void
  includeX?: boolean
  show?: boolean
  style?: AlertBannerTypes
  minHeight?: string
  textSize?: BodyTextSize
  paddingTop?: number
  icon?: ReactNode
  trailingLink?: React.ReactElement
  testID?: string
}

const ContentOuterContainer = styled(Animated.View)({
  position: 'relative',
  overflow: 'hidden',
  zIndex: -1,
  height: 0,
})

const ContentInnerContainer = styled(View)<{
  backgroundColor: string
  minHeight: string
  theme: ThemeType
  paddingTop?: number
}>(({ backgroundColor, minHeight, theme, paddingTop }) => ({
  position: 'absolute',
  width: '100%',
  bottom: theme.spacing['0px'],
  alignItems: 'center',
  backgroundColor,
  flexDirection: 'row',
  justifyContent: 'space-between',
  ...(paddingTop
    ? { paddingTop: `${paddingTop}px` }
    : {
        paddingVertical: getBreakpointValue(theme.breakpoints, {
          isMinWidthDesktop: '30px',
          isMinWidthTablet: '16px',
          default: '12px',
        }),
        paddingHorizontal: getBreakpointValue(theme.breakpoints, {
          isMinWidthDesktop: '32px',
          isMinWidthTablet: '16px',
          default: '12px',
        }),
      }),
  ...(paddingTop ? { paddingBottom: `10px` } : {}),
  zIndex: 1,
  minHeight,
}))

const CentralizedContent = styled.View({
  flex: 1,
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
})

const TextAndLinkContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  ...(theme.breakpoints.isMobileSized
    ? { flex: 1, flexDirection: 'column', alignItems: 'flex-start' }
    : { flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }),
}))

const StyledBodyText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  paddingRight: theme.spacing['12px'],
}))

const IconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  justifyContent: 'center',
  alignItems: 'center',
  marginRight: theme.spacing['8px'],
}))

const IconButtonContainer = styled.View(({ theme }) => ({
  margin: theme.breakpoints.isMobileSized ? `0 ${theme.spacing['12px']} ${theme.spacing['12px']} 0` : 'auto',
}))

export const AlertBanner = forwardRef<AlertBannerHandle, AlertBannerProps>(
  (
    {
      autoDismissTimer,
      contents,
      dismissHandler,
      includeX = true,
      show,
      style = AlertBannerTypes.NOTIFICATION,
      minHeight = '100px',
      textSize: textSizeProp,
      paddingTop = 0,
      icon,
      trailingLink,
      testID = 'AlertBanner',
    },
    ref,
  ) => {
    const { colors, breakpoints } = useTheme()
    const textSize = textSizeProp ?? (breakpoints.isMobileSized ? BodyTextSize.DEFAULT : BodyTextSize.LARGE)
    const { danger, success, warning, info, notification, calm, neutral } = colors.components.alertBanner

    const alertTypeColors = {
      [AlertBannerTypes.DANGER]: danger.background,
      [AlertBannerTypes.SUCCESS]: success.background,
      [AlertBannerTypes.WARNING]: warning.background,
      [AlertBannerTypes.INFO]: info.background,
      [AlertBannerTypes.NOTIFICATION]: notification.background,
      [AlertBannerTypes.CALM]: calm.background,
      [AlertBannerTypes.NEUTRAL]: neutral.background,
    }

    const alertTypeTextColors = {
      [AlertBannerTypes.DANGER]: danger.text,
      [AlertBannerTypes.SUCCESS]: success.text,
      [AlertBannerTypes.WARNING]: warning.text,
      [AlertBannerTypes.INFO]: info.text,
      [AlertBannerTypes.NOTIFICATION]: notification.text,
      [AlertBannerTypes.CALM]: calm.text,
      [AlertBannerTypes.NEUTRAL]: neutral.text,
    }

    const CLOSE_ANIMATION_DURATION_IN_MS = 300
    const OPEN_ANIMATION_DURATION_IN_MS = 400

    const [isOpen, setIsOpen] = useState(show)
    const contentHeight = useRef<number>(0)

    const sharedHeight = useSharedValue(contentHeight.current || 0)
    const sharedRotation = useSharedValue(isOpen ? -180 : 0)

    const animatedStyles = useAnimatedStyle(() => {
      'worklet'
      return {
        height: sharedHeight.value,
      }
    })

    const animationTiming = {
      open: {
        duration: OPEN_ANIMATION_DURATION_IN_MS,
        easing: Easing.bezier(0.53, 0.08, 0.04, 1),
      },
      close: {
        duration: CLOSE_ANIMATION_DURATION_IN_MS,
        easing: Easing.bezier(0.9, -0.01, 0.68, 0.87),
      },
    }

    useImperativeHandle(ref, () => ({
      open: () => {
        setIsOpen(true)
      },
      close: () => {
        setIsOpen(false)
      },
    }))

    // Sync internal state with external show prop
    useEffect(() => {
      setIsOpen(show)
    }, [show])

    useEffect(() => {
      if (autoDismissTimer) {
        const timeout = setTimeout(() => {
          setIsOpen(false)
          dismissHandler && dismissHandler()
        }, autoDismissTimer)
        return () => {
          clearTimeout(timeout)
        }
      } else {
        return
      }
    }, [autoDismissTimer, dismissHandler])

    useEffect(() => {
      const newHeight = isOpen ? contentHeight.current : 0
      const newRotation = isOpen ? -180 : 0
      const timingOptions = isOpen ? animationTiming.open : animationTiming.close
      sharedHeight.value = withTiming(newHeight, timingOptions)
      sharedRotation.value = withTiming(newRotation, timingOptions)
    }, [animationTiming.close, animationTiming.open, isOpen, sharedHeight, sharedRotation])

    const onContentlayout = (e: LayoutChangeEvent) => {
      const height = e.nativeEvent.layout.height
      contentHeight.current = height
      if (isOpen && Math.round(sharedHeight.value) !== Math.round(height)) {
        // Set shared height here so that content properly flows on resize when open
        sharedHeight.value = height
      }
    }

    const alertStyle = style || AlertBannerTypes.NOTIFICATION
    const textColor = alertTypeTextColors[alertStyle as AlertBannerTypes]
    const { formatMessage } = useIntl()

    return (
      <View aria-hidden={!isOpen}>
        <ContentOuterContainer
          style={animatedStyles}
          testID={tID(`${testID}-container`)}
          accessibilityRole='alert'
          role='alert'
          pointerEvents={isOpen ? 'auto' : 'none'}
        >
          <ContentInnerContainer
            onLayout={onContentlayout}
            accessibilityElementsHidden={!isOpen}
            accessibilityHidden={!isOpen}
            nativeID={'AlertBanner-container'}
            backgroundColor={alertTypeColors[alertStyle as AlertBannerTypes]}
            minHeight={minHeight}
            paddingTop={paddingTop}
          >
            <CentralizedContent>
              {icon && (
                <IconContainer>
                  {React.isValidElement<{ fillColor?: string }>(icon)
                    ? React.cloneElement(icon, { fillColor: textColor })
                    : icon}
                </IconContainer>
              )}
              <TextAndLinkContainer>
                <StyledBodyText color={textColor} size={textSize} text={contents} textAlign={'center'} />
                {trailingLink && (
                  <BodyText size={textSize} color={textColor} wrap>
                    {!breakpoints.isMobileSized && <>&nbsp;</>}
                    {React.cloneElement(trailingLink, { color: textColor, size: textSize })}
                  </BodyText>
                )}
              </TextAndLinkContainer>
            </CentralizedContent>
            {includeX && (
              <IconButtonContainer>
                <UnstyledIconButton
                  onPress={() => {
                    setIsOpen(false)
                    dismissHandler && dismissHandler()
                  }}
                  iconColor={textColor}
                  testID={tID(`${testID}-closeButton`)}
                  accessibilityLabel={formatMessage({
                    defaultMessage: 'Close Alert',
                    description: 'This button closes the alert banner',
                  })}
                  accessibilityElementsHidden={!isOpen}
                  importantForAccessibility={isOpen ? 'yes' : 'no-hide-descendants'}
                />
              </IconButtonContainer>
            )}
          </ContentInnerContainer>
        </ContentOuterContainer>
      </View>
    )
  },
)
